import { z } from "zod";

export const idSchema = z.coerce.number().int().positive();

/**
 * `YYYY-MM-DD`, and a *real* calendar date — not just the right shape.
 *
 * The regex alone accepts nonsense like `2026-13-45`. Parsing as a UTC
 * `Date` and round-tripping through `toISOString()` catches both an
 * out-of-range month/day (`2026-13-01`, `2026-01-32`) and a day that
 * doesn't exist in that month (`2026-02-30`, a non-leap `2026-02-29`) —
 * `Date` normalizes those forward (e.g. Feb 30 becomes Mar 2) instead of
 * throwing, so the round-trip string no longer matches the input.
 *
 * Callers that slice a validated value down to `YYYY-MM` (e.g. flooring an
 * `effectiveFrom` to the first of its month) can therefore trust the month
 * component is real — a downstream `date` column insert won't 500 on a
 * calendar-invalid value that only looked fine at the shape level.
 */
export const isoDateStringSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((v) => {
    const d = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
  }, "must be a real calendar date");

export const timestampsSchema = z.object({
  createdAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime().nullable(),
  deletedAt: z.string().datetime().nullable(),
});
export type Timestamps = z.infer<typeof timestampsSchema>;

export const localizedTextSchema = z.object({
  en: z.string(),
  fr: z.string(),
  ar: z.string(),
  // The four newer locales are optional so existing en/fr/ar payloads keep
  // validating; producers fill them as the data gets translated.
  es: z.string().optional(),
  it: z.string().optional(),
  de: z.string().optional(),
  nl: z.string().optional(),
});
export type LocalizedText = z.infer<typeof localizedTextSchema>;

export const requiredLocalizedTextSchema = z.object({
  en: z.string().min(1, "required"),
  fr: z.string(),
  ar: z.string(),
  es: z.string().optional(),
  it: z.string().optional(),
  de: z.string().optional(),
  nl: z.string().optional(),
});
export type RequiredLocalizedText = z.infer<typeof requiredLocalizedTextSchema>;

export function emptyLocalizedText(): LocalizedText {
  return { en: "", fr: "", ar: "" };
}

export const citySeoLocaleSchema = z.object({
  tagline: z.string().optional(),
  vibe: z.string().optional(),
  reason: z.string().optional(),
  bestTimeWindow: z.string().optional(),
  bestTimeNote: z.string().optional(),
  neighborhoods: z.array(z.string()).optional(),
});
export type CitySeoLocale = z.infer<typeof citySeoLocaleSchema>;

export const localizedCitySeoSchema = z.object({
  en: citySeoLocaleSchema.optional(),
  fr: citySeoLocaleSchema.optional(),
  ar: citySeoLocaleSchema.optional(),
  es: citySeoLocaleSchema.optional(),
  it: citySeoLocaleSchema.optional(),
  de: citySeoLocaleSchema.optional(),
  nl: citySeoLocaleSchema.optional(),
});
export type LocalizedCitySeo = z.infer<typeof localizedCitySeoSchema>;

export const categorySeoLocaleSchema = z.object({
  blurb: z.string().optional(),
  pluralShort: z.string().optional(),
  verb: z.string().optional(),
  highlights: z.array(z.string()).optional(),
});
export type CategorySeoLocale = z.infer<typeof categorySeoLocaleSchema>;

export const localizedCategorySeoSchema = z.object({
  en: categorySeoLocaleSchema.optional(),
  fr: categorySeoLocaleSchema.optional(),
  ar: categorySeoLocaleSchema.optional(),
  es: categorySeoLocaleSchema.optional(),
  it: categorySeoLocaleSchema.optional(),
  de: categorySeoLocaleSchema.optional(),
  nl: categorySeoLocaleSchema.optional(),
});
export type LocalizedCategorySeo = z.infer<typeof localizedCategorySeoSchema>;

/**
 * Drops `.default()` wrappers from an object shape, keeping the inner type.
 *
 * Zod's `.partial()` wraps every field in `ZodOptional` but leaves any inner
 * `ZodDefault` intact — so parsing a partial object that omits a defaulted
 * field still synthesizes that field's default value in the output. Any
 * schema built this way for a PATCH-style update is unsafe: a caller that
 * only sends the fields it wants to change gets back extra synthesized keys
 * for everything it left out, and if that output is fed straight to a
 * repository update, those synthesized values silently overwrite the real
 * ones already stored.
 *
 * This bit reservations: `reservationUpdateSchema` used to be
 * `reservationCreateBaseObject.partial()` directly, so a reschedule PATCH
 * carrying only `reservationDate`/`reservationTime` also emitted
 * `adults: 1`, `children: 0`, `commission: "0"`, `transport: false`,
 * `source: "admin"`, … which `repo.update` then wrote as if the caller had
 * asked for them — a reschedule reset a 2-guest booking down to 1 adult.
 * Stripping the defaults before calling `.partial()` keeps an omitted key
 * omitted.
 */
export function withoutDefaults<T extends z.ZodRawShape>(
  shape: T,
): { [K in keyof T]: T[K] extends z.ZodDefault<infer Inner> ? Inner : T[K] } {
  return Object.fromEntries(
    Object.entries(shape).map(([key, schema]) => [
      key,
      schema instanceof z.ZodDefault ? schema.unwrap() : schema,
    ]),
  ) as { [K in keyof T]: T[K] extends z.ZodDefault<infer Inner> ? Inner : T[K] };
}
